import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Checkbox, Form, Radio } from "antd";
import AddressForm from "../../customer-address/address-form.component";
import CheckoutCart from "./checkout-cart";
import CheckoutSummary from "./checkout-summary";
import { useSelector, useDispatch } from "react-redux";
import cartSelector from "../../../redux/selectors/cart.selector";
import currencySelector from "../../../redux/selectors/currency.selector";
import customerSelector from "../../../redux/selectors/customer.selector";
import commonService from "../../../services/common-service";
import Breadcrumb from "../../../wrappers/breadcrumb/Breadcrumb";
import checkoutProcessService from "./checkout-service";
import OrderActions from "../../../redux/actions/order.actions";
import { useHistory } from "react-router";
import utilActions from "../../../redux/actions/util.actions";
import utilSelector from "../../../redux/selectors/util.selector";
import CartActions from "../../../redux/actions/cart.actions";
import orderSelector from "../../../redux/selectors/order.selector";
import Coupon from "../cart-page/coupon/coupon";
import actionTypes from "../../../redux/action-types";
import CheckoutAddress from "./checkout-address";
import CheckoutCustomerInfo from "./checkout-customer-info";

const CheckoutRoot = ({ orderLoading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const cartList = useSelector(cartSelector.cartList);
  const currency = useSelector(currencySelector.currency);
  const symbol = useSelector(currencySelector.symbol);
  const rate = useSelector(currencySelector.rate);
  const subTotal = useSelector(cartSelector.subTotal);
  const total = useSelector(cartSelector.total);
  const discount = useSelector(cartSelector.discount);
  const couponDiscount = useSelector(cartSelector.couponDiscount);
  const totalDiscount = useSelector(cartSelector.totalDiscount);
  const shippingCharge = useSelector(cartSelector.shippingCharge);
  const defaultTotal = useSelector(cartSelector.defaultTotal);

  const codLimit = useSelector(utilSelector.codLimit);
  const countryList = useSelector(utilSelector.countryList);
  const selectedSC = useSelector(utilSelector.selectedSC);

  const isCustomerLoggedIn = useSelector(customerSelector.isCustomerLoggedIn);
  const customerInfo = useSelector(customerSelector.customerInfo);
  const isCouponApplied = useSelector(cartSelector.isCouponApplied);
  const coupon = useSelector(cartSelector.coupon);

  const [form] = Form.useForm();

  const [isBillingSameAsShipping, updateIsBillingSameAsShipping] =
    useState(true);

  const [billingPhoneValue, updateBillingPhoneValue] = useState("");
  const [billingPhoneError, updateBillingPhoneError] = useState("");

  const [shippingPhoneValue, updateShippingPhoneValue] = useState("");
  const [shippingPhoneError, updateShippingPhoneError] = useState("");
  const [ipAddress, updateIpAddress] = useState(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [customerData, updateCustomerData] = useState(null);
  const [customerErrorInfo, updateCustomerErrorInfo] = useState({
    firstName: '', lastName: '', email: ''
  });

  useEffect(() => {
    commonService.scrollToTop();
    dispatch(utilActions.updateRoutesAction("/checkout"));
    dispatch(utilActions.getSCCountriesAction());
    dispatch(CartActions.updateShippingChargeAction(0));
    dispatch({
      type: actionTypes.RESET_SC,
    });
    fetchIpAddress();
  }, []);

  const fetchIpAddress = () => {
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => {
        // Display the IP address on the screen
        updateIpAddress(data?.ip);
      })
      .catch((error) => {
        console.error("Error fetching IP address:", error);
      });
  };

  const updatePhone = (type, value) => {
    if (type === "shipping") {
      updateShippingPhoneValue(value);
      if (value.isValid) {
        updateShippingPhoneError("");
      } else {
        updateShippingPhoneError("Enter a valid number");
      }
    } else {
      if (value.isValid) {
        updateBillingPhoneValue(value);
        updateBillingPhoneError("");
      } else {
        updateBillingPhoneError("Enter a valid number");
      }
    }
  };

  const confirmOrder = (values) => {
    // validating order
    // If validation returns true proceed with order

    if (
      isCustomerLoggedIn &&
      (!customerData?.firstName ||
        !customerData?.lastName ||
        !customerData?.email)
    ) {
      updateCustomerErrorInfo({
        firstName: customerData?.firstName ? "": "Please enter name",
        lastName: customerData?.lastName ? "": "Please enter name",
        email: customerData?.email ? "": "Please enter email",
      })
      commonService.showNotification("error", "Please enter all the details");
      return;
    }

    if (
      (shippingPhoneValue.isValid &&
        (isBillingSameAsShipping ||
          (!isBillingSameAsShipping && billingPhoneValue.isValid))) ||
      (selectedShippingAddress &&
        (isBillingSameAsShipping ||
          (!isBillingSameAsShipping && selectedBillingAddress)))
    ) {
      if (
        checkoutProcessService.validateOrder(
          values,
          selectedSC,
          codLimit,
          total,
        )
      ) {
        checkoutProcessService.confirmOrder({
          values,
          shippingPhoneValue,
          billingPhoneValue,
          cartList,
          subTotal,
          total,
          discount,
          totalDiscount,
          couponDiscount,
          shippingCharge,
          isBillingSameAsShipping,
          dispatch,
          OrderActions,
          history,
          isCustomerLoggedIn,
          customerInfo,
          currency,
          rate,
          symbol,
          isCouponApplied,
          coupon,
          selectedSC,
          additionalInfo: commonService
            .getEncryption(ipAddress || "")
            .toString(),
          selectedBillingAddress,
          selectedShippingAddress,
          customerData,
        });
      } else {
        checkoutProcessService.codError();
      }
    } else {
      checkoutProcessService.phoneError(shippingPhoneValue, billingPhoneValue, updateBillingPhoneError, updateShippingPhoneError);
    }
  };

  const updateSelectedCountry = (e, type) => {
    if (type === "shipping") {
      form.setFieldsValue({
        paymentType: "",
      });
      if (e && countryList?.length > 0) {
        const id = countryList.find((country) => country.country === e)?.id;
        dispatch(utilActions.getSCByIdAction(id));
      }
    }
  };

  const calculateAdditionalShipping = () => {
    let totalOrderAmount = defaultTotal;
    let additionalShippingAmount = 0;
    while (totalOrderAmount > selectedSC.baseAmountLimit) {
      additionalShippingAmount += selectedSC.additionalAmount;
      totalOrderAmount -= selectedSC.baseAmountLimit;
    }
    return additionalShippingAmount;
  };

  const getShippingCharge = () => {
    let charge = 0;
    if (selectedSC !== null && selectedSC.country !== "India") {
      charge =
        defaultTotal > selectedSC.baseAmountLimit
          ? selectedSC.baseAmount + calculateAdditionalShipping()
          : selectedSC.baseAmount;
    }
    dispatch(CartActions.updateShippingChargeAction(charge));
  };

  useEffect(() => {
    getShippingCharge();
  }, [selectedSC]);

  useEffect(() => {
    // if (customerInfo !== null) {
    //   form.setFieldsValue({
    //     shipping_firstName: customerInfo.firstName,
    //     shipping_lastName: customerInfo.lastName,
    //     shipping_email: customerInfo.email,
    //     shipping_phone: customerInfo.mobile,
    //   });
    //   updateShippingPhoneValue({
    //     value: `${customerInfo.user.countryCode}${customerInfo.user.mobile}`,
    //     format2: `${customerInfo.user.countryCode} ${customerInfo.user.mobile}`,
    //     isValid: true,
    //     format1: customerInfo.user.mobile,
    //   });
    // } else {
    //   form.setFieldsValue({
    //     shipping_firstName: "",
    //     shipping_lastName: "",
    //     shipping_email: "",
    //     shipping_phone: "",
    //   });
    // }
  }, [customerInfo]);

  const fillValues = () => {
    form.setFieldsValue({
      shipping_firstName: "test",
      shipping_lastName: "test1",
      shipping_email: "hem.r10@gmail.com",
      shipping_phone: "9999999999",

      shipping_city: "chennai",
      shipping_address: "chennai",
      shipping_state: "tn",
      shipping_country: "India",
      shipping_pinCode: "600035",
    });
  };

  useEffect(() => {
    if (selectedShippingAddress && countryList?.length > 0) {
      const id = countryList.find(
        (country) => country.country === selectedShippingAddress?.country,
      )?.id;
      dispatch(utilActions.getSCByIdAction(id));
    }
  }, [selectedShippingAddress, countryList]);

  return (
    <>
      {cartList && cartList.length >= 1 && (
        <Form
          onFinish={confirmOrder}
          initialValues={{
            isBillingSameAsShipping,
          }}
          layout="vertical"
          form={form}
        >
          <div className="layout-grid">
            <div className="main-content">
              {isCustomerLoggedIn && (
                <CheckoutCustomerInfo
                  customerInfo={customerInfo}
                  updateCustomerData={updateCustomerData}
                  isGuest={!isCustomerLoggedIn}
                  customerData={customerData}
                  isCheckoutPage={true}
                  customerErrorInfo={customerErrorInfo}
                  updateCustomerErrorInfo={updateCustomerErrorInfo}
                />
              )}

              {isCustomerLoggedIn && (
                <>
                  {/* <CheckoutCustomerInfo
                    customerInfo={customerInfo}
                    setShippingContactInfo={setShippingContactInfo}
                    isGuest={!isCustomerLoggedIn}
                  /> */}
                  {/* <h3 className="mt-20">Shipping Address</h3> */}
                  <CheckoutAddress
                    addresses={customerInfo?.addresses || []}
                    customerInfo={customerInfo}
                    setSelectedAddressType={setSelectedShippingAddress}
                    updatedSC={updateSelectedCountry}
                    addressType={"shipping"}
                    updateSelectedCountry={updateSelectedCountry}
                    title={"Shipping Address"}
                  />
                </>
              )}

              {!isCustomerLoggedIn && (
                <AddressForm
                  title="Shipping Address"
                  type="shipping"
                  validation
                  countryList={countryList}
                  updateSelectedCountry={updateSelectedCountry}
                  fillValues={fillValues}
                  history={window.location.hostname}
                  phoneError={shippingPhoneError}
                  updatePhoneValue={updatePhone}
                  phoneValue={shippingPhoneValue.value}
                />
              )}

              <div>
                <Form.Item
                  name="isBillingSameAsShipping"
                  valuePropName="checked"
                >
                  <Checkbox
                    style={{ marginTop: 10 }}
                    onChange={(e) =>
                      updateIsBillingSameAsShipping(e.target.checked)
                    }
                  >
                    {" "}
                    <p
                      style={{
                        fontSize: "0.95rem",
                        color: "rgb(102, 102, 102)",
                        fontWeight: 600,
                      }}
                    >
                      {" "}
                      Is Billing Address same as Shipping Address ?
                    </p>
                  </Checkbox>
                </Form.Item>
              </div>

              {isCustomerLoggedIn && !isBillingSameAsShipping && (
                <>
                  {/* <CheckoutAddress
                    addresses={customerInfo?.addresses || []}
                    customerId={customerInfo?.customerId}
                    setSelectedAddressType={setSelectedShippingAddress}
                  /> */}

                  <CheckoutAddress
                    addresses={customerInfo?.addresses || []}
                    customerId={customerInfo?.customerId}
                    setSelectedAddressType={setSelectedBillingAddress}
                    updateSelectedCountry={updateSelectedCountry}
                    updatedSC={updateSelectedCountry}
                    addressType={"billing"}
                    title={"Billing Address"}
                  />
                </>
              )}
              {!isCustomerLoggedIn && !isBillingSameAsShipping && (
                <div
                  className={!isBillingSameAsShipping ? `show-form` : `no-form`}
                >
                  <AddressForm
                    title="Billing Address"
                    type="billing"
                    validation={!isBillingSameAsShipping}
                    countryList={countryList}
                    updateSelectedCountry={updateSelectedCountry}
                    history=""
                    phoneError={billingPhoneError}
                    updatePhoneValue={updatePhone}
                    phoneValue={billingPhoneValue.format2}
                  />
                </div>
              )}
            </div>
            <div className="sidebar">
              <Coupon
                cartList={cartList}
                rate={rate}
                currency={currency}
                symbol={symbol}
              />

              <div className="order-summary-card">
                <h3 className="order-title">Your order</h3>
                <div className="summary-table-header">
                  <span>Product</span>
                  <span>Total</span>
                </div>
                <CheckoutCart
                  cartItems={cartList}
                  symbol={symbol}
                  rate={rate}
                  currency={currency}
                />
                <CheckoutSummary
                  symbol={symbol}
                  rate={rate}
                  currency={currency}
                  subTotal={subTotal}
                  total={total}
                  discount={discount}
                  couponDiscount={couponDiscount}
                  totalDiscount={totalDiscount}
                  shippingCharge={shippingCharge}
                />

                <div className="payment-section">
                  {/* <h4><span>*</span> Payment Mode</h4> */}
                  <Form.Item
                    name="paymentType"
                    label="Payment Mode"
                    rules={[
                      {
                        required: true,
                        message: "Please select payment method",
                      },
                    ]}
                  >
                    <Radio.Group id="radioo">
                      {(selectedSC == null ||
                        selectedSC.country === "India") && (
                        <Radio value="COD" className="payment-option">
                          Cash on Delivery
                        </Radio>
                      )}

                      <Radio className="payment-option" value="PAYMENT_GATEWAY">
                        Credit Card / Debit Card / Net Banking / Other Wallets
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </div>
                <button
                  type="submit"
                  className="place-order-btn"
                  disabled={orderLoading}
                >
                  PLACE ORDER
                </button>
              </div>

              {/* <div 
                  className={`payment-option ${paymentMode === 'cod' ? 'active' : ''}`}
                  onClick={() => setPaymentMode('cod')}
                >
                  <input 
                    type="radio" 
                    name="payment" 
                    id="cod" 
                    checked={paymentMode === 'cod'} 
                    onChange={() => {}} 
                  />
                  <label htmlFor="cod">Cash on Delivery</label>
                </div>

                <div 
                  className={`payment-option ${paymentMode === 'online' ? 'active' : ''}`}
                  onClick={() => setPaymentMode('online')}
                >
                  <input 
                    type="radio" 
                    name="payment" 
                    id="online" 
                    checked={paymentMode === 'online'} 
                    onChange={() => {}} 
                  />
                  <label htmlFor="online">Cards / Net Banking / Wallets</label>
                </div> */}
            </div>
          </div>
        </Form>
      )}
    </>
  );
};

export default CheckoutRoot;

//  <div className="order-summary-card">
//               <h3 className="order-title">Your order</h3>

//               <div className="summary-table-header">
//                 <span>Product</span>
//                 <span>Total</span>
//               </div>

//               <div className="summary-item">
//                 <span className="product-name">WOODEN OWL</span>
//                 <span className="product-price">Rs 1,400</span>
//               </div>

//               <div className="shipping-row">
//                 <span>Shipping</span>
//               </div>

//               <div className="pricing-breakdown">
//                 <div className="price-row">
//                   <span className="label">Sub total</span>
//                   <span className="val">Rs 1,400</span>
//                 </div>
//                 <div className="price-row">
//                   <span className="label">Shipping Charge</span>
//                   <span className="val">Free Shipping</span>
//                 </div>
//                 <div className="price-row total">
//                   <span>Total</span>
//                   <span className="val">Rs 1,400</span>
//                 </div>
//               </div>

//               <div className="payment-section">
//                 <h4><span>*</span> Payment Mode</h4>

//                 <div
//                   className={`payment-option ${paymentMode === 'cod' ? 'active' : ''}`}
//                   onClick={() => setPaymentMode('cod')}
//                 >
//                   <input
//                     type="radio"
//                     name="payment"
//                     id="cod"
//                     checked={paymentMode === 'cod'}
//                     onChange={() => {}}
//                   />
//                   <label htmlFor="cod">Cash on Delivery</label>
//                 </div>

//                 <div
//                   className={`payment-option ${paymentMode === 'online' ? 'active' : ''}`}
//                   onClick={() => setPaymentMode('online')}
//                 >
//                   <input
//                     type="radio"
//                     name="payment"
//                     id="online"
//                     checked={paymentMode === 'online'}
//                     onChange={() => {}}
//                   />
//                   <label htmlFor="online">Cards / Net Banking / Wallets</label>
//                 </div>
//               </div>

//               <button className="place-order-btn">
//                 PLACE ORDER
//               </button>
//             </div>
